import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { FaStar, FaCheckCircle, FaTimesCircle, FaChevronRight } from 'react-icons/fa';

const offers = [
  {
    id: 'allbritishcasino',
    name: 'All British Casino',
    image: '/images/offers/allbritishcasino.webp',
    bonus: '100% up to £100',
    rating: 4.8,
    minDeposit: '£10',
    wagering: '35x',
    payoutTime: '24-48h',
    games: 600,
    mobile: true,
    license: 'UKGC'
  },
  {
    id: 'partycasino',
    name: 'PartyCasino',
    image: '/images/offers/partycasino.webp',
    bonus: '200% up to £200',
    rating: 4.7,
    minDeposit: '£20',
    wagering: '30x',
    payoutTime: '12-24h',
    games: 1000,
    mobile: true,
    license: 'UKGC'
  },
  {
    id: 'plazaroyal',
    name: 'Plaza Royal',
    image: '/images/offers/plazaroyal.webp',
    bonus: '150% up to £150',
    rating: 4.6,
    minDeposit: '£15',
    wagering: '40x',
    payoutTime: '24-72h',
    games: 750,
    mobile: true,
    license: 'UKGC'
  }
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Table',
  about: 'UK Casino Offers Comparison',
  name: 'Compare Best UK Casino Bonuses and Offers'
};

export const Comparison = () => {
  return (
    <>
      <SEO
        title="Compare UK Casino Offers - Side by Side Comparison"
        description="Compare the best UK casino offers side by side. Compare bonuses, wagering requirements, payout times, and more to find your perfect casino."
        canonical="/comparison"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen pt-20 pb-20">
        <section className="py-12 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                Compare Casino Offers
              </h1>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Side-by-side comparison of the best UK casino bonuses, features, and terms
              </p>
            </div>

            {/* Mobile Cards View */}
            <div className="md:hidden space-y-4">
              {offers.map((offer, index) => (
                <div
                  key={offer.id}
                  className="bg-dark-800 border border-gray-700 rounded-xl p-4 hover:border-primary-500 transition-all duration-300"
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <img
                      src={offer.image}
                      alt={offer.name}
                      className="w-16 h-16 rounded-lg object-contain flex-shrink-0"
                      loading="lazy"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold text-lg">{offer.name}</div>
                      <div className="text-xs text-gray-400">{offer.license}</div>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <FaStar className="text-yellow-400" />
                      <span className="text-white font-semibold">{offer.rating}</span>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <span className="bg-primary-500/20 text-primary-400 px-3 py-1 rounded-full text-sm font-semibold">
                      {offer.bonus}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                    <div>
                      <div className="text-gray-400 text-xs mb-1">Min Deposit</div>
                      <div className="text-white font-semibold">{offer.minDeposit}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-xs mb-1">Wagering</div>
                      <div className="text-white font-semibold">{offer.wagering}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-xs mb-1">Payout</div>
                      <div className="text-white font-semibold">{offer.payoutTime}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-xs mb-1">Games</div>
                      <div className="text-white font-semibold">{offer.games}+</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-400 text-sm">Mobile:</span>
                      {offer.mobile ? (
                        <FaCheckCircle className="text-green-400" />
                      ) : (
                        <FaTimesCircle className="text-red-400" />
                      )}
                    </div>
                  </div>

                  <Link
                    to={`/offers/${offer.id}`}
                    className="block w-full bg-gradient-to-r from-primary-500 to-accent-500 text-white text-center font-semibold py-3 rounded-lg hover:from-primary-600 hover:to-accent-600 transition-all duration-200"
                  >
                    View Offer
                  </Link>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block bg-dark-800 rounded-2xl border border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-dark-900">
                    <tr>
                      <th className="px-6 py-4 text-left text-white font-semibold">Casino</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Bonus</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Rating</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Min Deposit</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Wagering</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Payout</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Games</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Mobile</th>
                      <th className="px-6 py-4 text-center text-white font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offers.map((offer, index) => (
                      <tr
                        key={offer.id}
                        className={`border-t border-gray-700 hover:bg-dark-900/50 transition-colors ${
                          index % 2 === 0 ? 'bg-dark-800' : 'bg-dark-800/50'
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <img
                              src={offer.image}
                              alt={offer.name}
                              className="w-16 h-16 rounded-lg object-contain"
                              loading="lazy"
                            />
                            <div>
                              <div className="text-white font-semibold">{offer.name}</div>
                              <div className="text-xs text-gray-400">{offer.license}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="bg-primary-500/20 text-primary-400 px-3 py-1 rounded-full text-sm font-semibold">
                            {offer.bonus}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center space-x-1">
                            <FaStar className="text-yellow-400" />
                            <span className="text-white font-semibold">{offer.rating}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center text-gray-300">{offer.minDeposit}</td>
                        <td className="px-6 py-4 text-center text-gray-300">{offer.wagering}</td>
                        <td className="px-6 py-4 text-center text-gray-300">{offer.payoutTime}</td>
                        <td className="px-6 py-4 text-center text-gray-300">{offer.games}+</td>
                        <td className="px-6 py-4 text-center">
                          {offer.mobile ? (
                            <FaCheckCircle className="text-green-400 mx-auto" />
                          ) : (
                            <FaTimesCircle className="text-red-400 mx-auto" />
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Link
                            to={`/offers/${offer.id}`}
                            className="inline-block bg-gradient-to-r from-primary-500 to-accent-500 text-white px-4 py-2 rounded-lg hover:from-primary-600 hover:to-accent-600 transition-all duration-200 text-sm font-semibold"
                          >
                            View Offer
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Comparison Tips */}
            <div className="mt-12 grid md:grid-cols-3 gap-6">
              <div className="bg-dark-800 border border-gray-700 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-3">Compare Bonuses</h3>
                <p className="text-gray-400 text-sm">
                  Look beyond the bonus amount. Check wagering requirements, maximum bet limits, and game restrictions.
                </p>
              </div>
              <div className="bg-dark-800 border border-gray-700 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-3">Check Payout Speed</h3>
                <p className="text-gray-400 text-sm">
                  Faster payouts mean quicker access to your winnings. E-wallets typically process faster than bank transfers.
                </p>
              </div>
              <div className="bg-dark-800 border border-gray-700 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-3">Game Selection</h3>
                <p className="text-gray-400 text-sm">
                  Ensure your favorite games are available. Check for slots, table games, live casino, and specialty games.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

